/**
 * Machine page › Reliability — SPC, TARP, FMEA and control plan for one
 * machine.
 *
 * Reviewer feedback (Paul Telford, 25 Sep 2026): "The SPC module that Miles
 * is building for OptiFlow would work with the data being collected on these
 * machines, we may also be able to use the FMEA, Control Plans & TARPs for
 * this as well."
 *
 * Four sub-views behind a segmented control, addressable as `?view=`:
 *
 * | View | What |
 * |---|---|
 * | `spc` | I-MR chart of the machine's key metric (live replay, else daily mean), limits, spec, run-rule signals, MR chart, stats |
 * | `tarp` | The current level on every TARP that covers the machine, and the full ladder |
 * | `fmea` | The class's failure modes by RPN, with how O and D were derived |
 * | `control` | The class's control plan; each reaction plan links to its TARP |
 *
 * Data: `useReliability()`.
 *
 * UI layer.
 *
 * @see src/versions/v1/modules/fleet/MachineReliabilityTab.transform.ts
 * @see docs/logic/fleet/machine-reliability.md
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plug } from 'lucide-react';
import { HelpAnchor, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { useReliability } from '../../shared/reliability/useReliability';
import type { ReliabilityCtx } from '../../shared/reliability/useReliability';
import { LineChart } from '../../shared/viz/LineChart';
import { TabLoader } from './TabLoader';
import {
  controlPlanView,
  fmeaView,
  parseReliabilityView,
  RELIABILITY_VIEWS,
  spcView,
  tarpView,
  type ReliabilityViewId,
  type SpcView,
  type TarpCardView,
} from './MachineReliabilityTab.transform';

export interface MachineReliabilityTabProps {
  machineId: string;
}

const CARD =
  'rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg';

export function MachineReliabilityTab({ machineId }: MachineReliabilityTabProps) {
  const { ctx, loading, error } = useReliability();
  const [params, setParams] = useSearchParams();
  const view = parseReliabilityView(params.get('view'));

  const setView = (next: ReliabilityViewId) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('tab', 'reliability');
        if (next === 'spc') p.delete('view');
        else p.set('view', next);
        return p;
      },
      { replace: true },
    );
  };

  return (
    <div className="@container space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Commentable name="ReliabilityViews">
          <div
            role="tablist"
            aria-label="Reliability view"
            className="inline-flex rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface)] p-0.5"
          >
            {RELIABILITY_VIEWS.map((v) => {
              const on = v.id === view;
              return (
                <button
                  key={v.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setView(v.id)}
                  className={cn(
                    'rounded-md px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
                    on
                      ? 'bg-[var(--glass-surface-strong)] text-zinc-900 shadow-sm dark:text-zinc-100'
                      : 'text-muted hover:text-zinc-900 dark:hover:text-zinc-100',
                  )}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        </Commentable>
        <HelpAnchor doc="fleet/machine-reliability" />
      </div>

      <TabLoader loading={loading || !ctx} error={error} what="Reliability data" rows={10}>
        {ctx ? (
          <>
            {view === 'spc' ? <SpcPanel machineId={machineId} ctx={ctx} /> : null}
            {view === 'tarp' ? <TarpPanel machineId={machineId} ctx={ctx} /> : null}
            {view === 'fmea' ? <FmeaPanel machineId={machineId} ctx={ctx} /> : null}
            {view === 'control' ? (
              <ControlPlanPanel machineId={machineId} ctx={ctx} onTarp={() => setView('tarp')} />
            ) : null}
          </>
        ) : null}
      </TabLoader>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SPC
// ---------------------------------------------------------------------------

const STATE_CLASS: Readonly<Record<SpcView['stateTone'], string>> = {
  crit: 'band-vpoor',
  good: 'band-good',
  muted: 'band-fair',
};

function SpcPanel({ machineId, ctx }: { machineId: string; ctx: ReliabilityCtx }) {
  const spc = useMemo(() => spcView(machineId, ctx), [machineId, ctx]);
  if (!spc) {
    return (
      <div className={CARD}>
        <p className="text-sm text-zinc-700 dark:text-zinc-300">
          {machineId} has no tracked metric and no live feed, so there is no series to chart.
        </p>
      </div>
    );
  }
  const wide = spc.basis === 'live';

  return (
    <Commentable name="ReliabilitySpc">
      <div className="space-y-4">
        <section className={CARD} aria-labelledby="spc-title">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 id="spc-title" className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                {spc.title}
              </h3>
              <p className="mt-0.5 text-[11px] text-muted">{spc.subtitle}</p>
            </div>
            <span className={`band-pill ${STATE_CLASS[spc.stateTone]}`}>{spc.stateLabel}</span>
          </div>

          <dl className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-[var(--glass-border)] bg-[var(--glass-border)] @xl:grid-cols-3 @3xl:grid-cols-5">
            {spc.stats.map((s) => (
              <div key={s.label} className="bg-[var(--glass-surface-strong)] px-3 py-2">
                <dt className="text-[10px] uppercase tracking-wider text-muted">{s.label}</dt>
                <dd className="mono mt-0.5 text-base font-semibold text-zinc-900 dark:text-zinc-100">
                  {s.value}
                </dd>
                {s.hint ? <dd className="text-[10.5px] text-muted">{s.hint}</dd> : null}
              </div>
            ))}
          </dl>

          {spc.insufficientNote ? (
            <p className="mt-3 rounded-lg border border-dashed border-[var(--glass-border)] px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300">
              {spc.insufficientNote}
            </p>
          ) : null}

          <p className="mb-1 mt-4 text-[11px] font-medium uppercase tracking-wider text-muted">
            Individuals
          </p>
          <LineChart
            labels={spc.labels}
            series={[{ values: spc.values, color: 'var(--ink-secondary)', width: 1.3, dots: !wide }]}
            hlines={spc.hlines}
            markers={spc.markers}
            min={spc.domain.min}
            max={spc.domain.max}
            xticks={spc.xticks}
            width={960}
            height={wide ? 280 : 220}
            xTitle={spc.xTitle}
            yTitle={`${spc.title.split(' · ')[0]} (${spc.unit})`}
            ariaLabel={`${spc.title}: ${spc.stateLabel}`}
          />

          <p className="mb-1 mt-3 text-[11px] font-medium uppercase tracking-wider text-muted">
            Moving range
          </p>
          <LineChart
            labels={spc.labels}
            series={[{ values: spc.mr, color: 'var(--info)', width: 1.1, dots: !wide }]}
            hlines={spc.mrHlines}
            min={0}
            xticks={spc.xticks}
            width={960}
            height={wide ? 150 : 140}
            yTitle={`MR (${spc.unit})`}
            ariaLabel="Moving range chart"
          />
          <Legend basis={spc.basis} hasSignals={spc.markers.length > 0} insufficient={spc.state === 'insufficient'} />
        </section>

        {spc.signals.length ? (
          <section aria-labelledby="spc-signals" className="space-y-2">
            <h3 id="spc-signals" className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Run-rule signals{' '}
              <span className="font-normal text-muted">
                · Western Electric rules, numbered as Nelson; rule 2 at nine in a row
              </span>
            </h3>
            <Table>
              <THead>
                <TR>
                  <TH>When</TH>
                  <TH className="text-right">Value</TH>
                  <TH>Rule</TH>
                  <TH>What it means</TH>
                </TR>
              </THead>
              <TBody>
                {spc.signals.map((s) => (
                  <TR key={s.key}>
                    <TD className="mono whitespace-nowrap">{s.at}</TD>
                    <TD className="mono whitespace-nowrap text-right">{s.value}</TD>
                    <TD className="whitespace-nowrap">
                      <span className="band-pill band-vpoor">{s.rules}</span>
                    </TD>
                    <TD className="text-xs text-zinc-700 dark:text-zinc-300">{s.description}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </section>
        ) : null}

        <p className="flex gap-2 text-[11px] leading-relaxed text-muted">
          <Plug className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            Engine: {spc.engineLabel}. The chart is drawn from a plain series-in, chart-out
            interface so the SPC module Miles is building for OptiFlow can replace the local I-MR
            calculation without changing this page. Limits are phase-I, from the first 20 points.
            {spc.basis === 'live'
              ? ' The raw brake series is cyclic (loaded descent, return, loading); a per-descent peak series is the better production input.'
              : ''}
          </span>
        </p>
      </div>
    </Commentable>
  );
}

function Legend({
  basis,
  hasSignals,
  insufficient,
}: {
  basis: 'live' | 'daily';
  hasSignals: boolean;
  insufficient: boolean;
}) {
  const item = (swatch: React.ReactNode, label: string) => (
    <span className="inline-flex items-center gap-1.5">
      {swatch}
      {label}
    </span>
  );
  const rule = (color: string, dashed: boolean) => (
    <i
      aria-hidden
      className={cn('inline-block h-0 w-4 border-t-2', dashed && 'border-dashed')}
      style={{ borderColor: color }}
    />
  );
  return (
    <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-muted">
      {item(rule('var(--ink-secondary)', false), basis === 'live' ? 'sample' : 'daily mean')}
      {item(rule('var(--info)', false), insufficient ? 'mean' : 'centre line')}
      {insufficient ? null : item(rule('var(--warn)', true), 'control limits ±3σ')}
      {item(rule('var(--crit)', true), 'spec limit')}
      {hasSignals
        ? item(
            <i aria-hidden className="inline-block h-2.5 w-2.5 rounded-full border-2 border-[var(--crit)]" />,
            'run-rule signal (R1 beyond 3σ, R4 two of three beyond 2σ)',
          )
        : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TARP
// ---------------------------------------------------------------------------

function TarpPanel({ machineId, ctx }: { machineId: string; ctx: ReliabilityCtx }) {
  const cards = useMemo(() => tarpView(machineId, ctx), [machineId, ctx]);
  if (!cards.length) {
    return (
      <div className={CARD}>
        <p className="text-sm text-zinc-700 dark:text-zinc-300">
          No TARP covers {machineId}'s class yet.
        </p>
      </div>
    );
  }
  return (
    <Commentable name="ReliabilityTarp">
      <div className="space-y-6">
        {cards.map((c) => (
          <TarpCard key={c.tarpId} card={c} />
        ))}
      </div>
    </Commentable>
  );
}

function TarpCard({ card }: { card: TarpCardView }) {
  return (
    <section aria-labelledby={`tarp-${card.tarpId}`} className="space-y-3">
      <div
        className={cn(CARD, 'border-l-4')}
        style={{ borderLeftColor: card.colour }}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="mono text-[11px] text-muted">{card.tarpId}</p>
            <h3
              id={`tarp-${card.tarpId}`}
              className="text-sm font-semibold text-zinc-900 dark:text-zinc-100"
            >
              {card.title}
            </h3>
          </div>
          <span className={`band-pill ${card.levelClass} text-xs`}>{card.level}</span>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-xs @2xl:grid-cols-4">
          <Fact label="Since">{card.since ?? 'the start of the window'}</Fact>
          <Fact label="Latest value">{card.latest ?? '— (event basis)'}</Fact>
          <Fact label="Judged on">{card.basis}</Fact>
          <Fact label="Owner">{card.owner}</Fact>
        </dl>
        <p className="mt-3 text-xs text-zinc-700 dark:text-zinc-300">
          <span className="font-medium text-zinc-900 dark:text-zinc-100">Trigger · </span>
          {card.trigger}
        </p>
        <ul className="mt-2 space-y-1">
          {card.actions.map((a) => (
            <li key={a} className="flex gap-2 text-xs text-zinc-700 dark:text-zinc-300">
              <span aria-hidden className="mt-[7px] h-1 w-1 shrink-0 rounded-full" style={{ background: card.colour }} />
              {a}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted">
          The ladder · {card.component}
        </p>
        <ol className="grid grid-cols-1 gap-2 @2xl:grid-cols-2 @5xl:grid-cols-4">
          {card.ladder.map((r) => (
            <li
              key={r.level}
              aria-current={r.current ? 'step' : undefined}
              className={cn(
                'rounded-xl border bg-[var(--glass-surface)] p-3',
                r.current ? 'border-2' : 'border-[var(--glass-border)] opacity-80',
              )}
              style={r.current ? { borderColor: r.colour } : undefined}
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`band-pill ${r.levelClass}`}>{r.level}</span>
                {r.current ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">
                    Current
                  </span>
                ) : null}
              </div>
              <p className="mt-2 text-xs text-zinc-900 dark:text-zinc-100">{r.trigger}</p>
              <ul className="mt-2 space-y-1">
                {r.actions.map((a) => (
                  <li key={a} className="text-[11px] leading-snug text-zinc-700 dark:text-zinc-300">
                    · {a}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[10.5px] uppercase tracking-wider text-muted">Owner · {r.owner}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-muted">{label}</dt>
      <dd className="mono mt-0.5 text-zinc-900 dark:text-zinc-100">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FMEA
// ---------------------------------------------------------------------------

function FmeaPanel({ machineId, ctx }: { machineId: string; ctx: ReliabilityCtx }) {
  const rows = useMemo(() => fmeaView(machineId, ctx), [machineId, ctx]);
  if (!rows.length) {
    return (
      <div className={CARD}>
        <p className="text-sm text-zinc-700 dark:text-zinc-300">
          No FMEA rows are seeded for {machineId}'s class yet.
        </p>
      </div>
    );
  }
  return (
    <Commentable name="ReliabilityFmea">
      <section className="space-y-2" aria-labelledby="fmea-title">
        <h3 id="fmea-title" className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Failure modes for this class{' '}
          <span className="font-normal text-muted">
            · RPN = severity × occurrence × detection, highest first
          </span>
        </h3>
        <Table>
          <THead>
            <TR>
              <TH className="text-right">RPN</TH>
              <TH>Failure mode</TH>
              <TH className="text-center">S</TH>
              <TH className="text-center">O</TH>
              <TH className="text-center">D</TH>
              <TH>How O and D were derived</TH>
              <TH>Recommended action</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((r) => (
              <TR key={r.id}>
                <TD className="text-right align-top">
                  <span className={`band-pill ${r.rpnClass} mono`}>{r.rpn}</span>
                </TD>
                <TD className="min-w-[14rem] align-top">
                  <p className="text-xs font-medium text-zinc-900 dark:text-zinc-100">
                    {r.component} · {r.failureMode}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted">
                    Effect: {r.effect}. Cause: {r.cause}.
                  </p>
                  <p className="mono mt-0.5 text-[10.5px] text-muted">
                    {r.id}
                    {r.machineScore !== null ? ` · ${machineId} condition ${r.machineScore}/100` : ''}
                  </p>
                </TD>
                <TD className="mono text-center align-top">{r.s}</TD>
                <TD className="mono text-center align-top">{r.o}</TD>
                <TD className="mono text-center align-top">{r.d}</TD>
                <TD className="min-w-[14rem] align-top text-[11px] text-zinc-700 dark:text-zinc-300">
                  <p>
                    <b className="font-medium">O</b> · {r.occurrenceHow}
                  </p>
                  <p className="mt-1">
                    <b className="font-medium">D</b> · {r.detectionHow}
                  </p>
                </TD>
                <TD className="min-w-[12rem] align-top text-[11px] text-zinc-700 dark:text-zinc-300">
                  <p>{r.recommendedAction}</p>
                  <p className="mt-1 text-[10.5px] uppercase tracking-wider text-muted">
                    Owner · {r.owner}
                  </p>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        <p className="text-[11px] text-muted">
          Severity, base detection, failure modes and actions are proof-of-concept seeds for a
          reliability engineer to confirm; occurrence comes from this fleet's work-order history
          since 28 Feb 2025.
        </p>
      </section>
    </Commentable>
  );
}

// ---------------------------------------------------------------------------
// Control plan
// ---------------------------------------------------------------------------

function ControlPlanPanel({
  machineId,
  ctx,
  onTarp,
}: {
  machineId: string;
  ctx: ReliabilityCtx;
  onTarp: () => void;
}) {
  const rows = useMemo(() => controlPlanView(machineId, ctx), [machineId, ctx]);
  if (!rows.length) {
    return (
      <div className={CARD}>
        <p className="text-sm text-zinc-700 dark:text-zinc-300">
          No control plan is seeded for {machineId}'s class yet.
        </p>
      </div>
    );
  }
  return (
    <Commentable name="ReliabilityControlPlan">
      <section className="space-y-2" aria-labelledby="cp-title">
        <h3 id="cp-title" className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Control plan{' '}
          <span className="font-normal text-muted">
            · what is measured, against what, how often, and what happens when it is out
          </span>
        </h3>
        <Table>
          <THead>
            <TR>
              <TH>Characteristic</TH>
              <TH>Method</TH>
              <TH>Spec</TH>
              <TH>Frequency</TH>
              <TH>Reaction plan</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((r) => (
              <TR key={r.key}>
                <TD className="min-w-[12rem] align-top">
                  <p className="text-xs font-medium text-zinc-900 dark:text-zinc-100">
                    {r.characteristic}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted">{r.component}</p>
                </TD>
                <TD className="align-top text-[11px] text-zinc-700 dark:text-zinc-300">{r.method}</TD>
                <TD className="mono align-top text-[11px] text-zinc-900 dark:text-zinc-100">
                  {r.specLimit}
                </TD>
                <TD className="align-top text-[11px] text-zinc-700 dark:text-zinc-300">
                  {r.frequency}
                </TD>
                <TD className="min-w-[14rem] align-top text-[11px] text-zinc-700 dark:text-zinc-300">
                  <p>{r.reactionPlan}</p>
                  {r.tarpId ? (
                    <button
                      type="button"
                      onClick={onTarp}
                      className="group mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-medium text-accent-700 dark:text-accent-400"
                    >
                      <span className="mono underline underline-offset-2 group-hover:no-underline">
                        {r.tarpId}
                      </span>
                      {r.tarpLevel ? (
                        <span className={`band-pill ${r.tarpLevelClass}`}>
                          {machineId} at {r.tarpLevel}
                        </span>
                      ) : null}
                    </button>
                  ) : null}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        <p className="text-[11px] text-muted">
          Proof-of-concept control plan: the brake and hydraulic limits are the demo's; the other
          limits are plausible site values, to be replaced by the customer's own plan.
        </p>
      </section>
    </Commentable>
  );
}
